'use client';

import { Autocomplete, Box, TextField } from '@mui/material';
import { US_JURISDICTION_OPTIONS, type UsJurisdictionOption } from '@/lib/constants/usJurisdictionOptions';

export interface PiGeographyFiltersProps {
  /** Uppercase 2-letter code from the closed list, or empty string when unset. */
  state: string | undefined;
  /** Substring matched case-insensitively against PI institution on the server. */
  institution: string | undefined;
  onStateChange: (state: string | undefined) => void;
  onInstitutionChange: (institution: string | undefined) => void;
  /** MUI TextField size passed through. */
  size?: 'small' | 'medium';
  stateSx?: object;
  institutionSx?: object;
}

/**
 * PI-only geography filters: US jurisdiction (closed list) and institution contains.
 * Used by project search and sample-request search (not bulk sample search).
 */
export function PiGeographyFilters({
  state,
  institution,
  onStateChange,
  onInstitutionChange,
  size = 'small',
  stateSx,
  institutionSx,
}: PiGeographyFiltersProps) {
  const selected: UsJurisdictionOption | null =
    state?.trim() && US_JURISDICTION_OPTIONS.some((o) => o.code === state.trim().toUpperCase())
      ? (US_JURISDICTION_OPTIONS.find((o) => o.code === state.trim().toUpperCase()) ?? null)
      : null;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="flex-start"
      gap={2}
      sx={{ width: '100%', columnGap: 2, rowGap: 1.5 }}
    >
      <Autocomplete
        size={size}
        sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: 400, ...stateSx }}
        options={[...US_JURISDICTION_OPTIONS]}
        getOptionLabel={(o) => `${o.label} (${o.code})`}
        value={selected}
        onChange={(_, v) => onStateChange(v?.code)}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        renderInput={(params) => <TextField {...params} label="PI state" placeholder="Any" />}
      />
      <TextField
        size={size}
        sx={{ flex: '1 1 220px', minWidth: 180, maxWidth: 400, ...institutionSx }}
        label="PI institution (contains)"
        value={institution ?? ''}
        onChange={(e) => onInstitutionChange(e.target.value || undefined)}
        placeholder="Substring match"
      />
    </Box>
  );
}
